import { makeStyles, tokens, Title2, Body1, Caption1, Button } from '@fluentui/react-components';
import { ShieldProhibitedRegular } from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { useAccess } from './AccessContext';

/* Shown to an AUTHENTICATED user whose token was accepted by the API but who has no
   DorUsers row (i.e. genuinely not provisioned). The most common real cause is being
   signed into the wrong account, so the PRIMARY action switches accounts via
   `login({ prompt: 'select_account' })` (NOT a logout) — and we also offer Sign out and a
   diagnostic of what the app saw. (SPA-Auth Standard, rules 6 + 7.) */

const useStyles = makeStyles({
  page: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: tokens.spacingVerticalL,
    maxWidth: '32rem', // readable column width; no spacing token this large
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
  },
  icon: {
    fontSize: '48px', // glyph size, not layout spacing
    color: tokens.colorPaletteRedForeground1,
  },
  message: { color: tokens.colorNeutralForeground2 },
  account: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1 },
  actions: { display: 'flex', gap: tokens.spacingHorizontalM },
  diag: {
    marginTop: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalM,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground3,
    fontFamily: tokens.fontFamilyMonospace,
    maxWidth: '32rem',
    textAlign: 'left',
    overflowWrap: 'anywhere',
  },
});

export function NoAccessPage() {
  const styles = useStyles();
  const { displayName, login, logoutRedirect } = useAuth();
  const { diagnostics } = useAccess();

  return (
    <div className={styles.page}>
      <main className={styles.card} role="alert" aria-live="polite" data-testid="no-access">
        <ShieldProhibitedRegular className={styles.icon} aria-hidden />
        <Title2>You don&apos;t have access</Title2>
        <Body1 className={styles.message}>
          {displayName ? (
            <>
              You&apos;re signed in as <span className={styles.account}>{displayName}</span>, but this
              account isn&apos;t set up for DevOps Reports.
            </>
          ) : (
            <>This account isn&apos;t set up for DevOps Reports.</>
          )}{' '}
          If you have an account with access, sign in with that account — or ask an administrator to
          grant access.
        </Body1>
        <div className={styles.actions}>
          <Button
            appearance="primary"
            data-testid="switch-account"
            onClick={() => void login({ prompt: 'select_account' })}
          >
            Sign in with a different account
          </Button>
          <Button appearance="secondary" data-testid="sign-out" onClick={() => void logoutRedirect()}>
            Sign out
          </Button>
        </div>
        <Caption1 className={styles.diag} data-testid="no-access-diagnostics">
          diagnostics — account: {diagnostics.email ?? '(none)'} · profile: none (no DorUsers row)
          {diagnostics.tokenError ? ` · token: ${diagnostics.tokenError}` : ''}
        </Caption1>
      </main>
    </div>
  );
}
